/**
 * Centralized Notification Service
 * Provides consistent notifications across the application
 */

import ErrorHandlingService from './errorHandlingService';

export const NOTIFICATION_TYPES = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
};

class NotificationService {
    static listeners = [];
    static notificationQueue = [];
    static maxNotifications = 5;

    /**
     * Register a callback to be called when notifications are triggered
     * @param {Function} callback - Function to call with notification details
     */
    static onNotification(callback) {
        if (typeof callback === 'function') {
            this.listeners.push(callback);
        }
    }

    /**
     * Remove a notification listener
     * @param {Function} callback - The callback to remove
     */
    static removeListener(callback) {
        const index = this.listeners.indexOf(callback);
        if (index > -1) {
            this.listeners.splice(index, 1);
        }
    }

    /**
     * Show a notification
     * @param {string} message - The message to display
     * @param {string} type - The type of notification (success, error, warning, info)
     * @param {Object} options - Additional options. Set `contentSource` to
     *   `backend` for server-authored/localized copy so the UI bridge leaves it
     *   byte-for-byte unchanged; locally-authored interface copy is the default.
     */
    static show(message, type = NOTIFICATION_TYPES.INFO, options = {}) {
        const notification = {
            id: Date.now() + Math.random(),
            message,
            type,
            timestamp: new Date().toISOString(),
            duration: options.duration || this.getDefaultDuration(type),
            persistent: options.persistent || false,
            action: options.action || null,
            ...options
        };

        // Add to queue
        this.notificationQueue.push(notification);

        // Remove old notifications if queue is too long
        if (this.notificationQueue.length > this.maxNotifications) {
            this.notificationQueue.shift();
        }

        // Notify all listeners
        this.listeners.forEach(listener => {
            try {
                listener(notification, this.notificationQueue);
            } catch (error) {
                console.error('Error in notification listener:', error);
            }
        });

        // Auto-remove notification after duration (unless persistent)
        if (!notification.persistent) {
            setTimeout(() => {
                this.remove(notification.id);
            }, notification.duration);
        }

        return notification.id;
    }

    /**
     * Remove a notification by ID
     * @param {string|number} notificationId - The ID of the notification to remove
     */
    static remove(notificationId) {
        const index = this.notificationQueue.findIndex(n => n.id === notificationId);
        if (index > -1) {
            this.notificationQueue.splice(index, 1);

            // Notify listeners of the updated queue
            this.listeners.forEach(listener => {
                try {
                    listener(null, this.notificationQueue);
                } catch (error) {
                    console.error('Error in notification listener:', error);
                }
            });
        }
    }

    /**
     * Clear all notifications
     */
    static clear() {
        this.notificationQueue = [];
        this.listeners.forEach(listener => {
            try {
                listener(null, []);
            } catch (error) {
                console.error('Error in notification listener:', error);
            }
        });
    }

    /**
     * Show success notification
     * @param {string} message - Success message
     * @param {Object} options - Additional options
     */
    static success(message, options = {}) {
        return this.show(message, NOTIFICATION_TYPES.SUCCESS, options);
    }

    /**
     * Show error notification
     * @param {string|Error} error - Error message or Error object
     * @param {Object} options - Additional options
     */
    static error(error, options = {}) {
        let message;

        if (error instanceof Error) {
            // Handle error through error service
            const errorInfo = ErrorHandlingService.handleError(error, 'NOTIFICATION');
            message = ErrorHandlingService.getUserFriendlyMessage(errorInfo);
        } else if (typeof error === 'string') {
            message = error;
        } else {
            message = 'A apărut o eroare neașteptată.';
        }

        return this.show(message, NOTIFICATION_TYPES.ERROR, {
            duration: 6000, // Longer duration for errors
            ...options
        });
    }

    /**
     * Show warning notification
     * @param {string} message - Warning message
     * @param {Object} options - Additional options
     */
    static warning(message, options = {}) {
        return this.show(message, NOTIFICATION_TYPES.WARNING, options);
    }

    /**
     * Show info notification
     * @param {string} message - Info message
     * @param {Object} options - Additional options
     */
    static info(message, options = {}) {
        return this.show(message, NOTIFICATION_TYPES.INFO, options);
    }

    /**
     * Get default duration based on notification type
     * @param {string} type - Notification type
     */
    static getDefaultDuration(type) {
        const durations = {
            [NOTIFICATION_TYPES.SUCCESS]: 3000,
            [NOTIFICATION_TYPES.ERROR]: 6000,
            [NOTIFICATION_TYPES.WARNING]: 4000,
            [NOTIFICATION_TYPES.INFO]: 4000
        };

        return durations[type] || 4000;
    }

    /**
     * Get current notification queue
     */
    static getQueue() {
        return [...this.notificationQueue];
    }

    /**
     * Show a confirmation dialog (alternative to alert/confirm)
     * @param {string} message - Confirmation message
     * @param {Object} options - Options with onConfirm and onCancel callbacks
     */
    static confirm(message, options = {}) {
        return this.show(message, NOTIFICATION_TYPES.WARNING, {
            persistent: true,
            action: {
                type: 'confirm',
                confirmText: options.confirmText || 'Da',
                cancelText: options.cancelText || 'Anulează',
                onConfirm: options.onConfirm || (() => {}),
                onCancel: options.onCancel || (() => {})
            }
        });
    }

    /**
     * Utility method to replace alert() calls
     * @param {string} message - Alert message
     * @param {string} type - Type of alert (defaults to info)
     */
    static alert(message, type = NOTIFICATION_TYPES.INFO) {
        return this.show(message, type, { duration: 5000 });
    }
}

// Convenience functions for global use
export const showNotification = (message, type, options) => NotificationService.show(message, type, options);
export const showSuccess = (message, options) => NotificationService.success(message, options);
export const showError = (error, options) => NotificationService.error(error, options);
export const showWarning = (message, options) => NotificationService.warning(message, options);
export const showInfo = (message, options) => NotificationService.info(message, options);
export const showConfirm = (message, options) => NotificationService.confirm(message, options);

export default NotificationService;
